<template>
	<v-dialog v-model="modalOpen" max-width="500px">
		<v-card>
			<v-card-title>Invite a Supplier</v-card-title>
			<v-card-subtitle class="my-2">
				Enter the supplier's name. We'll check if their company already has an
				account, then figure out the right way to add them.
			</v-card-subtitle>

			<v-card-text>
				<v-form ref="form" v-model="valid">

					<!-- STEP 1: company name lookup -->
					<v-alert
						v-if="infoMessage"
						type="info"
						dense
						text
						class="mb-3"
						@click="infoMessage = null"
					>
						{{ infoMessage }}
					</v-alert>
					<div v-if="step === 1">
						<div style="position: relative;" ref="nameWrapper">
							<v-text-field
								label="Name of supplier"
								hide-details="auto"
								placeholder="Enter name here"
								outlined
								dense
								:rules="notEmptyRules"
								v-model="name"
								@input="onNameInput"
								autocomplete="off"
							/>

							<!-- Name Suggestion dropdown — only show if there are actual results -->
							<v-card
								v-if="showNameSuggestions && filteredByName.length > 0"
							>
								<v-list dense class="py-0" style="max-height: 200px; overflow-y: auto;">
									<v-list-item
										v-for="client in filteredByName"
										:key="client.ClientID"
										@click="selectClient(client)"
									>
										<v-list-item-avatar>
											<v-avatar color="primary" size="36">
												<v-img v-if="client.CompanyLogo" :src="client.CompanyLogo"></v-img>
												<span v-else class="white--text">{{ client.ClientName.charAt(0) }}</span>
											</v-avatar>
										</v-list-item-avatar>
										<v-list-item-content>
											<v-list-item-title>{{ client.ClientName }}</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</v-card>
						</div>
					</div>

					<!-- STEP 2: email, branches depending on whether a company was found -->
					<div v-else>
						<div class="d-flex align-center mb-3">
							<v-btn icon small @click="goToPreviousStep()">
								<v-icon>mdi-arrow-left</v-icon>
							</v-btn>
							<span class="ml-1 text-body-2">
								<template v-if="companyAvailable">
									Adding a new contact to <strong>{{ selectedClient.ClientName }}</strong>
								</template>
								<template v-else>
									Inviting <strong>{{ name }}</strong> as a new company
								</template>
							</span>
						</div>

						<div style="position: relative;" ref="emailWrapper">
							<v-text-field
								label="Email Address"
								hide-details="auto"
								placeholder="Enter email here"
								outlined
								dense
								:rules="emailRules"
								v-model="email"
								@input="onEmailInput"
								@blur="onEmailBlur"
								autocomplete="off"
							></v-text-field>

							<!-- Email Suggestion dropdown — only show if there are actual results -->
							<v-card
								v-if="showEmailSuggestions && filteredByEmail.length > 0"
							>
								<v-list dense class="py-0" style="max-height: 200px; overflow-y: auto;">
									<v-list-item
										v-for="client in filteredByEmail"
										:key="client.Email"
										@click="selectEmailSuggestion(client)"
									>
										<v-list-item-avatar>
											<v-avatar color="primary" size="36">
												<v-img v-if="client.CompanyLogo" :src="client.CompanyLogo"></v-img>
												<span v-else class="white--text">{{ client.ClientName.charAt(0) }}</span>
											</v-avatar>
										</v-list-item-avatar>
										<v-list-item-content>
											<v-list-item-title>{{ client.Email }}</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</v-card>
						</div>

						<!-- Expiry only applies when we're generating a brand new invitation code -->
						<v-select
							v-if="!companyAvailable"
							class="mt-5"
							outlined
							:items="expiryItems"
							label="Expiry date"
							item-text="text"
							item-value="value"
							:rules="notEmptyRules"
							v-model="expiry"
						></v-select>
					</div>

				</v-form>
			</v-card-text>

			<v-card-actions>
				<template v-if="step === 1">
					<v-btn color="primary" text outlined :disabled="!valid" @click="goToEmailStep()">Next</v-btn>
					<v-btn color="error" text outlined @click="closeModal()">Cancel</v-btn>
				</template>

				<template v-else>
					<v-btn
						v-if="companyAvailable && !emailExistsForCompany"
						color="primary"
						text
						outlined
						:disabled="!valid"
						:to="'/team/add'"
					>
						Add as a team member
					</v-btn>

					<v-btn
						v-else-if="!companyAvailable"
						color="primary"
						text
						outlined
						:disabled="!valid || emailExistsAnywhere"
						@click="createCode()"
					>
						Create
					</v-btn>

					<v-btn text outlined @click="goToPreviousStep()">Back</v-btn>
					<v-btn color="error" text outlined @click="closeModal()">Cancel</v-btn>
				</template>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import InvitationcodeAPI from "../../api/connections/invitationcodes"
import ConnectionAPI from "../../api/connections/connection"
import LoadingState from "../../consts/LoadingState"
import { mapActions } from "vuex"
export default {
	name: "AddNew",

	data() {
		return {
			expiry: null,
			name: null,
			email: null,
			valid: false,
			multipleRedeem: false,
			notEmptyRules: [(v) => !!v || "This field cannot be empty"],
			emailRules: [
				(v) => !!v || "This field cannot be empty",
				(v) => {
					if (!v) return true 
					const atIndex = v.indexOf("@")
					if (atIndex <= 0) return "Enter a valid email address"
					const domain = v.slice(atIndex + 1)
					const validDomain = /^[^.\s]+(\.[^.\s]+)+$/.test(domain)
					return validDomain || "Enter a valid email address"
				},
			],
			// Search state
			apiClients: [],
			hasFetchedData: false,
			showNameSuggestions: false,
			showEmailSuggestions: false,

			// Wizard state
			step: 1, // 1 = name/company lookup, 2 = email
			selectedClient: null, // the matched company row, if any
			companyAvailable: null, // null = not yet resolved, otherwise true/false
			infoMessage: null,
		}
	},
	computed: {
		expiryItems() {
			return [
				{ text: "1 month (30 days)", value: 2592000000 },
				{ text: "3 months (90 days)", value: 7776000000 },
			]
		},

		// Filter by name or ClientID
		filteredByName() {
			if (!this.name || this.name.length < 1) return []
			const query = this.name.toLowerCase()
			const matches = this.apiClients.filter(
				(client) =>
					client.ClientName.toLowerCase().includes(query) ||
					String(client.ClientID).includes(query)
			)

			// One row per contact in apiClients means the same company can appear
			// multiple times — collapse to one suggestion per company.
			const seen = new Set()
			return matches.filter((client) => {
				if (seen.has(client.ClientID)) return false
				seen.add(client.ClientID)
				return true
			})
		},

		// Filter by full email OR domain — scoped to the matched company when we have one,
		// otherwise searched across everyone (helps surface a company we missed by name)
		filteredByEmail() {
			if (!this.email || this.email.length < 1) return []
			const query = this.email.toLowerCase()

			let candidates = this.apiClients
			if (this.companyAvailable && this.selectedClient) {
				candidates = candidates.filter(
					(client) => client.ClientID === this.selectedClient.ClientID
				)
			}

			return candidates.filter((client) => {
				if (!client.Email) return false
				const clientEmail = client.Email.toLowerCase()
				const clientDomain = clientEmail.includes("@")
					? clientEmail.split("@")[1]
					: ""

				return (
					clientEmail.includes(query) ||
					clientDomain.includes(query) ||
					clientDomain.includes(query.replace("@", ""))
				)
			})
		},

		// Does the email typed in step 2 already belong to a user under the selected company?
		emailExistsForCompany() {
			if (!this.selectedClient || !this.email) return false
			const query = this.email.trim().toLowerCase()
			if (!query) return false
			return this.apiClients.some(
				(client) =>
					client.ClientID === this.selectedClient.ClientID &&
					client.Email &&
					client.Email.toLowerCase() === query
			)
		},
		emailExistsAnywhere() {
			if (!this.email) return false

			const query = this.email.trim().toLowerCase()

			return this.apiClients.some(
				(client) =>
					client.Email &&
					client.Email.toLowerCase() === query
			)
		},

		modalOpen: {
			get() {
				return (
					this.$store.getters[
						"connections/invitationcodes/getInvitationCodeModalOpen"
					] === true
				)
			},
			set(value) {
				this.setInvitationCodeModalOpen(value)
			},
		},
	},
	mounted() {
		document.addEventListener("click", this.handleClickOutside)
	},
	beforeDestroy() {
		document.removeEventListener("click", this.handleClickOutside)
	},
	methods: {
		// Closes whichever suggestion dropdown is open if the click landed outside its wrapper
		handleClickOutside(event) {
			const nameWrapper = this.$refs.nameWrapper
			if (nameWrapper && !nameWrapper.contains(event.target)) {
				this.showNameSuggestions = false
			}

			const emailWrapper = this.$refs.emailWrapper
			if (emailWrapper && !emailWrapper.contains(event.target)) {
				this.showEmailSuggestions = false
			}
		},

		onNameInput() {
			this.infoMessage = null
			this.selectedClient = null
			this.companyAvailable = null

			if (this.name && this.name.length >= 1) {
				this.showNameSuggestions = true
				this.fetchClients()
			} else {
				this.showNameSuggestions = false
			}
		},

		onEmailInput() {
			this.infoMessage = null
			if (this.email && this.email.length >= 1) {
				this.showEmailSuggestions = true
				this.fetchClients()
			} else {
				this.showEmailSuggestions = false
			}
		},

		// Fetches the full connections list ONCE and caches it. All searching/filtering
		// after that happens client-side via filteredByName / filteredByEmail above —
		// that's why this no longer takes a search term: passing the in-progress query
		// to the API was the bug, since whatever matched the very first keystroke got
		// cached as "the data" and every later keystroke just re-filtered that stale,
		// partial list instead of the full one.
		fetchClients() {
			if (this.hasFetchedData) return Promise.resolve()
			return ConnectionAPI.getExistingConnections()
				.then((data) => {			
					this.apiClients = data
					this.hasFetchedData = true
				})
		},

		selectClient(client) {
			// Picking a suggestion means the company is a confirmed match —
			// jump straight to email entry for the *new* contact at that company.
			this.name = client.ClientName
			this.selectedClient = client
			this.companyAvailable = true
			this.email = null
			this.showNameSuggestions = false
			this.showEmailSuggestions = false
			this.infoMessage = null
			this.step = 2
		},

		async goToEmailStep() {
			if (!this.name) return

			await this.fetchClients()

			if (!this.selectedClient) {
				const query = this.name.trim().toLowerCase()
				const match = this.apiClients.find(
					(client) => client.ClientName.toLowerCase() === query
				)
				this.selectedClient = match || null
			}

			this.companyAvailable = !!this.selectedClient
			this.showNameSuggestions = false
			this.showEmailSuggestions = false
			this.email = null
			this.step = 2
		},

		goToPreviousStep(message = null) {
			this.step = 1
			this.email = null
			this.showNameSuggestions = true
			this.showEmailSuggestions = false
			this.infoMessage = message
		},

		onEmailBlur() {
			if (this.step !== 2 || !this.email) return

			// Existing company + existing email
			if (this.companyAvailable && this.emailExistsForCompany) {
				this.infoMessage = `An account with the email "${this.email}" already exists for ${this.selectedClient.ClientName}.`
				this.showEmailSuggestions = false
				return
			}

			// New company + email already exists elsewhere
			if (!this.companyAvailable && this.emailExistsAnywhere) {
				this.infoMessage =
					`This email is already registered. Please use another email address.`
				this.showEmailSuggestions = false
			}
		},

		// Picking an email suggestion means that exact account already exists —
		// route back to step 1 so they can pick the right company/person there.
		selectEmailSuggestion(client) {
			this.email = client.Email
			this.showEmailSuggestions = false
			this.infoMessage = `This email is already registered. Please use another email address.`
		},

		// Closing just closes the dialog and reloads the page — a fresh page load
		// resets every bit of local state for us, so there's nothing left to do here.
		closeModal() {
			this.modalOpen = false
			window.location.reload()
		},

		codeCreated(code) {
			this.$emit("updateInvitationCode", code)
		},
		createCode() {
			if (!this.companyAvailable && this.emailExistsAnywhere) {
				this.infoMessage =
					"This email is already registered. Please use another email address."
				return
			}
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)

			this.setCreatedInvitationCode({ invitationcode: null })
			InvitationcodeAPI.createInvitationCode(
				this.expiry,
				this.name,
				this.multipleRedeem
			)
				.then((result) => {
					this.$store.dispatch("loading/" + LoadingState.ACTION_END_LOADING, {
						root: true,
					})
					this.setCreatedInvitationCode({
						invitationcode: result.invitationCode,
					})
					this.codeCreated(result.invitationCode)
					this.modalOpen = false
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		...mapActions("connections/invitationcodes", [
			"setCreatedInvitationCode",
			"setInvitationCodeModalOpen",
		]),
	},
}
</script>

<style></style>